import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageLoader } from "../components/PageLoader";
import UserService from "../services/UserService";
import { PageLayout } from "../components/PageLayout";
import { initAmplitude, sendAmplitudeData, setAmplitudeUserId } from "../common/amplitude";
import { ROUTES } from "../config/routes";

export const SubscriptionSuccess = () => {
    const [searchParams] = useSearchParams();
    const { user, getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("Verifying your subscription");

    useEffect(() => {
        const sessionId = searchParams.get("session_id");
        
        if (!sessionId) {
            setMessage("No session ID found. Redirecting...");
            setTimeout(() => navigate(ROUTES.PRICING), 2000);
            return;
        }

        const processSuccess = async () => {
            try {
                setMessage("Payment successful! Finalizing your subscription...");

                // Ensure user is authenticated so we can call the API
                if (!isAuthenticated) {
                    await loginWithRedirect({
                        appState: { returnTo: window.location.pathname + window.location.search },
                    });
                    return;
                }
                
                // Call backend confirm endpoint (bullet‑proof fallback to webhook)
                let hasActiveSubscription = false;
                let isFirstSubscription = false;
                try {
                    const serverToken = await getAccessTokenSilently();
                    const confirmResponse = await UserService.confirmSubscriptionSession(sessionId, serverToken);
                    
                    // Check if subscription is active
                    if (confirmResponse && confirmResponse.success && confirmResponse.status === true) {
                        hasActiveSubscription = true;
                        console.log("✅ Subscription confirmed and active:", confirmResponse.subscriptionType);
                        
                        // Check if this is the first subscription by checking all subscriptions count
                        // If allSubSctiptions has only 1 item, it's their first subscription
                        if (confirmResponse.allSubSctiptions && confirmResponse.allSubSctiptions.length === 1) {
                            isFirstSubscription = true;
                            console.log("✅ This is the user's first subscription - redirecting to Profile");
                        } else {
                            // Also check by getting all subscriptions to be sure
                            try {
                                const allSubsResponse = await UserService.getUserSubscription(serverToken, user, true);
                                if (allSubsResponse && allSubsResponse.allSubSctiptions && allSubsResponse.allSubSctiptions.length === 1) {
                                    isFirstSubscription = true;
                                    console.log("✅ This is the user's first subscription (verified via getAllSubscriptions)");
                                }
                            } catch (subsError) {
                                console.log("Could not verify subscription count:", subsError);
                            }
                        }
                    } else {
                        console.log("⚠️ Subscription confirmed but not active yet:", confirmResponse);
                    }
                } catch (apiError) {
                    console.error("Error confirming subscription session:", apiError);
                    // We still continue; Pricing/Dashboard will re-check status
                }
                
                // Add analytics if user is available
                if (user && user.email) {
                    try {
                        initAmplitude();
                        setAmplitudeUserId(user.email);
                        sendAmplitudeData("Subscription Payment Completed", {
                            'email': user.email,
                            'session_id': sessionId,
                            'is_first_subscription': isFirstSubscription
                        });
                    } catch (analyticsError) {
                        console.log("Analytics error:", analyticsError);
                    }
                }
                
                // If subscription is active, go directly to Dashboard; otherwise go to Pricing
                if (hasActiveSubscription) {
                    if (isFirstSubscription) {
                        setMessage("Subscription confirmed! Setting up your profile...");
                        setTimeout(() => {
                            navigate(`${ROUTES.DASHBOARD}?tab=setup&subtab=profile`, { state: { refresh: true, fromPayment: true } });
                        }, 1500);
                    } else {
                        setMessage("Subscription confirmed! Redirecting to your dashboard...");
                        setTimeout(() => {
                            navigate(ROUTES.DASHBOARD, { state: { refresh: true, fromPayment: true } });
                        }, 1500);
                    }
                } else {
                    setMessage("Redirecting to your subscription...");
                    // Redirect to Pricing. Pricing/Dashboard can re-check subscription status post confirm.
                    setTimeout(() => {
                        navigate(ROUTES.PRICING, { state: { refresh: true, fromPayment: true } });
                    }, 1500);
                }
                
            } catch (error) {
                console.error("Error processing success:", error);
                // Even if there's an error, go to Pricing
                setMessage("Redirecting...");
                setTimeout(() => {
                    navigate(ROUTES.PRICING, { state: { refresh: true, fromPayment: true } });
                }, 1500);
            } finally {
                setLoading(false);
            }
        };

        processSuccess();
    }, [searchParams, navigate, user, isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

    return (
        <PageLayout>
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-md-6 text-center py-5">
                        {loading ? (
                            <>
                                <PageLoader />
                                <h4 className="mt-4">{message}</h4>
                            </>
                        ) : (
                            <div className="">
                                <h4 className="">Success!</h4>
                                <p>{message}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

// Don't use withAuthenticationRequired here - handle auth manually to prevent logout
export default SubscriptionSuccess;
